// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";

interface VersionInfo {
  version: string;
  gitSha: string;
}

/**
 * Checks whether a newer build has been deployed by fetching the un-hashed `version.json` emitted at
 * build time and comparing its `gitSha` against the one baked into this bundle (`__GIT_SHA__`).
 *
 * Rather than poll on a timer, this rides TanStack Query's focus/reconnect refetching — the check
 * runs when the user returns to the tab or the network reconnects, throttled by `staleTime`. In dev
 * the file doesn't exist (it's build-only), so the query simply errors and reports no update.
 */
export function useAppVersion(): { updateAvailable: boolean } {
  const { data } = useQuery({
    queryKey: ["app-version"],
    queryFn: async (): Promise<VersionInfo> => {
      const res = await fetch("/version.json", { cache: "no-store" });
      if (!res.ok) throw new Error(`version check failed: ${res.status}`);
      return res.json();
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 5 * 60 * 1000,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  return { updateAvailable: !!data && data.gitSha !== __GIT_SHA__ };
}
